import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import "../../Styles/PatientPaymentForm.css";

// Updated AutoCalculateFields to include MEDICINE_CHARGES
const AutoCalculateFields = ({ previousBalance }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const total =
      Number(values.CONSULTING_CHARGES || 0) +
      Number(values.PANCHKARMA_CHARGES || 0) +
      Number(values.MEDICINE_CHARGES || 0); // ✅ Added MEDICINE_CHARGES
    
    const newBalance =
      Number(previousBalance || 0) + total - Number(values.CURRENT_PAYMENT || 0);

    setFieldValue("TOTAL", total, false);
    setFieldValue("NEW_BALANCE", newBalance, false);
    setFieldValue("PREVIOUS_BALANCE", previousBalance, false);
  }, [
    previousBalance,
    values.CONSULTING_CHARGES,
    values.PANCHKARMA_CHARGES,
    values.MEDICINE_CHARGES, // ✅ Added to dependency array
    values.CURRENT_PAYMENT,
    setFieldValue,
  ]);

  return null;
};

// Custom Spinner component
const Spinner = () => <div className="spinner"></div>;

// Main component with custom CSS
const PatientPaymentForm = ({
  initialValues,
  onSubmit,
  fetchPreviousBalance,
}) => {
  const [previousBalance, setPreviousBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getBalance = async () => {
      try {
        const balance = await fetchPreviousBalance();
        setPreviousBalance(balance || 0);
      } catch (error) {
        console.error("Error fetching previous balance:", error);
        setPreviousBalance(0);
      } finally {
        setLoading(false);
      }
    };
    getBalance();
  }, [fetchPreviousBalance]);

  // Initial values for Formik - ✅ Added MEDICINE_CHARGES
  const formInitialValues = {
    CONSULTING_CHARGES: initialValues?.CONSULTING_CHARGES || 0,
    PANCHKARMA_CHARGES: initialValues?.PANCHKARMA_CHARGES || 0,
    MEDICINE_CHARGES: initialValues?.MEDICINE_CHARGES || 0, // ✅ Added
    CURRENT_PAYMENT: initialValues?.CURRENT_PAYMENT || 0,
    TOTAL: 0,
    NEW_BALANCE: 0,
    PREVIOUS_BALANCE: previousBalance || 0,
  };

  // Validation schema - ✅ Added MEDICINE_CHARGES validation
  const validationSchema = Yup.object().shape({
    CONSULTING_CHARGES: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
    PANCHKARMA_CHARGES: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
    MEDICINE_CHARGES: Yup.number() // ✅ Added
      .min(0, "Cannot be negative")
      .required("Required"),
    CURRENT_PAYMENT: Yup.number()
      .min(0, "Cannot be negative")
      .required("Required"),
  });

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="payment-form-container">
      <div className="payment-form-card">
        <div className="payment-form-header">
          <h4 className="header-title">Patient Payment Details</h4>
        </div>

        <div className="payment-form-body">
          <Formik
            enableReinitialize
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              onSubmit(values);
              setSubmitting(false);
            }}
          >
            {(formikProps) => (
              <Form className="payment-form">
                <AutoCalculateFields previousBalance={previousBalance} />

                <div className="form-row">
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="consultingCharges" className="form-label">
                        Consulting Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="consultingCharges"
                          name="CONSULTING_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${
                            formikProps.touched.CONSULTING_CHARGES &&
                            formikProps.errors.CONSULTING_CHARGES
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      </div>
                      <ErrorMessage
                        name="CONSULTING_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="panchkarmaCharges" className="form-label">
                        Panchkarma Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="panchkarmaCharges"
                          name="PANCHKARMA_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${
                            formikProps.touched.PANCHKARMA_CHARGES &&
                            formikProps.errors.PANCHKARMA_CHARGES
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      </div>
                      <ErrorMessage
                        name="PANCHKARMA_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  {/* ✅ NEW FIELD: Medicine Charges */}
                  <div className="form-col">
                    <div className="form-group">
                      <label htmlFor="medicineCharges" className="form-label">
                        Medicine Charges
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <Field
                          id="medicineCharges"
                          name="MEDICINE_CHARGES"
                          type="number"
                          placeholder="0"
                          className={`form-control ${
                            formikProps.touched.MEDICINE_CHARGES &&
                            formikProps.errors.MEDICINE_CHARGES
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      </div>
                      <ErrorMessage
                        name="MEDICINE_CHARGES"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="form-col-full">
                    <div className="form-group">
                      <label htmlFor="currentPayment" className="form-label">
                        Fees Received 
                      </label>
                      <div className="input-group">
                        <span className="input-group-text ">₹</span>
                        <Field
                          id="currentPayment"
                          name="CURRENT_PAYMENT"
                          type="number"
                          placeholder="0"
                          className={`form-control large-text ${
                            formikProps.touched.CURRENT_PAYMENT &&
                            formikProps.errors.CURRENT_PAYMENT
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      </div>
                      <ErrorMessage
                        name="CURRENT_PAYMENT"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>
                </div>

                <hr className="divider" />

                <h5 className="summary-title">Summary</h5>
                <div className="summary-row">
                  <div className="summary-box">
                    <h6 className="summary-box-title">Prev. Balance</h6>
                    <Field name="PREVIOUS_BALANCE">
                      {({ field }) => (
                        <h4 className="summary-box-value text-info">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>
                  
                  <div className="summary-box">
                    <h6 className="summary-box-title">Total Current Charges</h6>
                    <Field name="TOTAL">
                      {({ field }) => (
                        <h4 className="summary-box-value text-primary">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>

                  <div className="summary-box">
                    <h6 className="summary-box-title">New Balance</h6>
                    <Field name="NEW_BALANCE">
                      {({ field }) => (
                        <h4 className="summary-box-value text-success">
                          {field.value.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                            minimumFractionDigits: 0,
                          })}
                        </h4>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="button-container">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={formikProps.isSubmitting}
                  >
                    {formikProps.isSubmitting ? (
                      <>
                        <Spinner />
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Save Payment"
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default PatientPaymentForm;